import React from 'react';

import { faClock } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { UseFormReturn } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';
import { useHookFormMask } from 'use-mask-input';

import SettingsContext from 'src/features/administration/context/SettingsContext';
import { useGetPricingPlan } from 'src/features/pricingPlan/api/useGetPricingPlan';
import {
    CURRENCY_MAP,
    DAY_OF_WEEK_TRANSLATIONS_MAP,
    PAYMENT_TYPE_TRANSLATION_MAP,
} from 'src/features/pricingPlan/utils/constants';
import { PricingPlanPeriodAdjustmentFormSchemaValues } from 'src/features/pricingPlan/utils/formSchema';
import { pricingPlanMessages } from 'src/features/pricingPlan/utils/messages';
import { DAY_OF_WEEK, PAYMENT_TYPE } from 'src/features/pricingPlan/utils/types';
import { useParking } from 'src/providers/ParkingProvider';
import SelectFormField from 'src/registry/components/forms/SelectFormField';
import DatePickerWithTime from 'src/registry/ui/DatePickerWithTime';
import { Form, FormControl, FormControlStack, FormItem, FormLabel, FormMessage, FormField } from 'src/registry/ui/Form';
import { Input } from 'src/registry/ui/Input';

type AdjustmentFormProps = {
    form: UseFormReturn<PricingPlanPeriodAdjustmentFormSchemaValues>;
    onSubmit: (values: PricingPlanPeriodAdjustmentFormSchemaValues) => void;
};

function AdjustmentForm({ onSubmit, form }: AdjustmentFormProps) {
    const { formatMessage } = useIntl();

    const [displayAdjustmentAmount, setDisplayAdjustmentAmount] = React.useState('');
    const isEditingAdjustmentAmount = React.useRef(false);

    const adjustmentAmount = form.watch('adjustment_amount');

    const { planId = '' } = useParams();
    const { parkingId } = useParking();
    const { data: pricingPlan } = useGetPricingPlan({ planId, parkingId });
    const { default_currency } = React.useContext(SettingsContext);

    const currency = React.useMemo(
        () => (pricingPlan?.currency ? formatMessage(CURRENCY_MAP[pricingPlan.currency]) : default_currency),
        [default_currency, formatMessage, pricingPlan],
    );

    React.useEffect(() => {
        if (isEditingAdjustmentAmount.current) {
            return;
        }
        setDisplayAdjustmentAmount(adjustmentAmount != null ? (adjustmentAmount / 100).toFixed(2) : '');
    }, [form, adjustmentAmount]);

    const registerWithMask = useHookFormMask(form.register);

    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)} variant="dialog">
            <FormField
                control={form.control}
                name="valid_from"
                render={({ field }) => {
                    return (
                        <FormItem variant="dialog">
                            <FormLabel variant="dialog" required>
                                {formatMessage(pricingPlanMessages.validFrom)}
                            </FormLabel>
                            <FormControlStack>
                                <FormControl>
                                    <DatePickerWithTime
                                        onChange={field.onChange}
                                        dateISO={field.value}
                                        placeholder={`${formatMessage(pricingPlanMessages.validFrom)}...`}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormControlStack>
                        </FormItem>
                    );
                }}
            />
            <FormField
                control={form.control}
                name="valid_to"
                render={({ field }) => {
                    return (
                        <FormItem variant="dialog">
                            <FormLabel variant="dialog">{formatMessage(pricingPlanMessages.validTo)}</FormLabel>
                            <FormControlStack>
                                <FormControl>
                                    <DatePickerWithTime
                                        onChange={field.onChange}
                                        dateISO={field.value ?? undefined}
                                        placeholder={`${formatMessage(pricingPlanMessages.validTo)}...`}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormControlStack>
                        </FormItem>
                    );
                }}
            />

            <SelectFormField<PricingPlanPeriodAdjustmentFormSchemaValues>
                form={form}
                name="payment_type"
                label={formatMessage(pricingPlanMessages.paymentType)}
                options={Object.values(PAYMENT_TYPE)}
                optionTranslationMap={PAYMENT_TYPE_TRANSLATION_MAP}
            />

            <SelectFormField<PricingPlanPeriodAdjustmentFormSchemaValues>
                required={false}
                form={form}
                name="day_of_week"
                label={formatMessage(pricingPlanMessages.day)}
                options={Object.values(DAY_OF_WEEK)}
                optionTranslationMap={DAY_OF_WEEK_TRANSLATIONS_MAP}
            />
            <FormField
                control={form.control}
                name="from_time"
                render={() => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog" required>
                            <FormattedMessage {...pricingPlanMessages.fromHour} />
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <div className="relative flex min-w-32 items-center gap-2">
                                    <FontAwesomeIcon
                                        icon={faClock}
                                        size="sm"
                                        className="text-muted-foreground pointer-events-none absolute top-1/2 left-2 -translate-y-1/2"
                                    />
                                    <Input
                                        type="text"
                                        placeholder="00-23:59"
                                        inputMode="numeric"
                                        className="pl-8 text-sm"
                                        {...registerWithMask('from_time', 'datetime', {
                                            inputFormat: 'HH:mm',
                                            outputFormat: 'HH:mm',
                                            showMaskOnHover: false,
                                            placeholder: '0',
                                        })}
                                    />
                                </div>
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
            <FormField
                control={form.control}
                name="to_time"
                render={() => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog" required>
                            <FormattedMessage {...pricingPlanMessages.toHour} />
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <div className="relative flex w-full min-w-32 items-center gap-2">
                                    <FontAwesomeIcon
                                        icon={faClock}
                                        size="sm"
                                        className="text-muted-foreground pointer-events-none absolute top-1/2 left-2 -translate-y-1/2"
                                    />
                                    <Input
                                        type="text"
                                        placeholder="00-23:59"
                                        inputMode="numeric"
                                        className="pl-8 text-sm"
                                        {...registerWithMask('to_time', 'datetime', {
                                            inputFormat: 'HH:mm',
                                            outputFormat: 'HH:mm',
                                            showMaskOnHover: false,
                                            placeholder: '0',
                                        })}
                                    />
                                </div>
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
            <FormField
                control={form.control}
                name="adjustment_percent"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog">
                            <FormattedMessage {...pricingPlanMessages.adjustmentPercent} />
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <Input
                                    type="text"
                                    inputMode="decimal"
                                    placeholder={`${formatMessage(pricingPlanMessages.adjustmentPercent)}...`}
                                    value={field.value ?? ''}
                                    onChange={(e) => {
                                        const inputValue = e.target.value;
                                        if (inputValue === '' || /^-?\d{0,20}(?:\.\d{0,4})?$/.test(inputValue)) {
                                            field.onChange(inputValue);
                                        }
                                    }}
                                />
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />

            <FormField
                control={form.control}
                name="adjustment_amount"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog">
                            <FormattedMessage {...pricingPlanMessages.currencyAmount} values={{ currency }} />
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <Input
                                    type="text"
                                    inputMode="decimal"
                                    placeholder={`${formatMessage(pricingPlanMessages.currencyAmount, { currency })}...`}
                                    value={displayAdjustmentAmount}
                                    onChange={(e) => {
                                        const inputValue = e.target.value;

                                        if (!/^\d*\.?\d{0,2}$/.test(inputValue)) {
                                            return;
                                        }

                                        isEditingAdjustmentAmount.current = true;
                                        setDisplayAdjustmentAmount(inputValue);

                                        if (inputValue === '') {
                                            field.onChange(null);
                                            return;
                                        }

                                        const numeric = Number(inputValue);

                                        if (!Number.isNaN(numeric)) {
                                            field.onChange(Math.round(numeric * 100));
                                        }
                                    }}
                                />
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
        </Form>
    );
}

export default AdjustmentForm;
